import { uniq } from 'lodash';

import { ENTITY_PAGE_MAP } from 'hb-react/shared/utils/entitiesContentData/constants';
import { type EntityType } from 'hb-react/shared/utils/entitiesContentData/types';

type PageIdentifiersOfEntityTypes = NonNullable<
  (typeof ENTITY_PAGE_MAP)[keyof typeof ENTITY_PAGE_MAP]
>;

/**
 * Returns the page identifiers for the given entity types.
 *
 * @param entityTypes - The entity types to get the page identifiers for.
 * @returns The page identifiers for the given entity types.
 */
export const getPageIdentifiersForEntityTypes = (
  entityTypes: Array<EntityType>,
): Array<PageIdentifiersOfEntityTypes> =>
  uniq(
    entityTypes
      .map((entityType) => ENTITY_PAGE_MAP[entityType])
      .filter(Boolean),
  );

/**
 * Returns a record of page identifiers and their corresponding page names.
 *
 * @param entityTypeNames - The entity type names to get the page names for.
 * @returns A record of page identifiers and their corresponding page names.
 */
export const getPageNamesFromEntityTypeNames = (
  entityTypeNames: Record<string, string>,
): Record<string, string> =>
  Object.entries(entityTypeNames).reduce<Record<string, string>>(
    (acc, [entityType, entityTypeName]) => {
      const pageIdentifier = ENTITY_PAGE_MAP[entityType];
      if (pageIdentifier) {
        acc[pageIdentifier] = entityTypeName;
      }
      return acc;
    },
    {},
  );
